import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["tlds"],
  _excluded2 = ["tlds"];
import { RecommendationFeature } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { CoreDomainSearchStore } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useInfiniteQuery } from 'react-query';
import { createAdvancedQuery } from './useRecommendations.utils';
import { useDisplayPrices } from './useDisplayPrices';
import { useExactMatchPreview } from './useExactMatchPreview';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { getSharedQueryClient } from '@wix/premium-fed-sdk/@apis-infra-react';
import { ExperimentSpecs } from '../constants';
import { prefetchSuggestions, useSuggestions } from './useSuggestions';
export const DEFAULT_LIMIT = 10;
const DEFAULT_FEATURES = [RecommendationFeature.PRICING, RecommendationFeature.LABEL_MATCHING];

// PRICING is dropped from the recommendations call and resolved afterwards via
// `useDisplayPrices`, keeping the initial call lean for a faster
// time-to-first-result.
const resolveFeatures = features => features.filter(feature => feature !== RecommendationFeature.PRICING);
const LOCALLY_RESOLVED_FEATURES = [RecommendationFeature.LABEL_MATCHING];
const canCallSuggestDomainsDirectly = features => features.every(feature => LOCALLY_RESOLVED_FEATURES.includes(feature));
const EXPANDED_LIMIT = 50;
export const isExpandedGtldSuggestionsEnabled = () => {
  var _getPremiumEssentials;
  return Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.ExpandedGtldSuggestions]);
};
const getHttpErrorStatus = error => {
  var _httpError$response;
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const httpError = error;
  return ((_httpError$response = httpError.response) == null ? void 0 : _httpError$response.status) ?? httpError.status;
};
const classifyRecommendationsError = error => {
  if (error instanceof Error && error.message === 'Network Error') {
    return 'NetworkIssue';
  }
  if (getHttpErrorStatus(error) === 429) {
    return 'RateLimited';
  }
  return 'TechnicalIssue';
};
const classifyBiErrorSubtype = error => {
  if (error instanceof Error && error.message === 'Network Error') {
    return 'network';
  }
  const status = getHttpErrorStatus(error);
  if (status === 429) {
    return 'rate_limited';
  }
  return status !== undefined && status >= 500 ? 'server_error' : 'technical';
};
const DEFAULT_QUERY = 'mystunningwebsite';
const getRecommendationsQueryKey = _ref => {
  let query = _ref.query,
    features = _ref.features,
    tlds = _ref.tlds,
    hasValidVouchers = _ref.hasValidVouchers,
    uniqueSldsOnly = _ref.uniqueSldsOnly,
    productType = _ref.productType,
    limit = _ref.limit;
  return ['listRecommendations', query, features, tlds, hasValidVouchers, ...(limit ? [limit] : []), ...(uniqueSldsOnly ? [uniqueSldsOnly] : []), ...(productType ? [productType] : [])];
};
export const prefetchRecommendations = async function (_temp) {
  let _ref2 = _temp === void 0 ? {} : _temp,
    productType = _ref2.productType,
    hasValidVouchers = _ref2.hasValidVouchers;
  const _ref3 = CoreDomainSearchStore.getStore() ?? {},
    tlds = _ref3.tlds,
    submittedFilters = _objectWithoutPropertiesLoose(_ref3, _excluded);
  const query = createAdvancedQuery(submittedFilters) || DEFAULT_QUERY;
  const _ref4 = getPremiumEssentialsSafe() ?? {},
    httpClient = _ref4.httpClient;
  if (!httpClient) {
    return;
  }
  const queryClient = getSharedQueryClient();
  const baseFeatures = resolveFeatures(DEFAULT_FEATURES);
  const expandedLimit = isExpandedGtldSuggestionsEnabled() ? EXPANDED_LIMIT : undefined;
  const requestedFeatures = query === DEFAULT_QUERY ? [...baseFeatures, RecommendationFeature.USE_BUSINESS_INFO] : baseFeatures;
  if (canCallSuggestDomainsDirectly(requestedFeatures)) {
    prefetchSuggestions({
      query,
      tlds: tlds ?? [],
      limit: expandedLimit ?? DEFAULT_LIMIT,
      productType
    });
    return;
  }
  queryClient.prefetchInfiniteQuery({
    queryKey: getRecommendationsQueryKey({
      query,
      features: baseFeatures,
      tlds,
      hasValidVouchers,
      productType,
      limit: expandedLimit
    }),
    queryFn: () => {
      const features = [...baseFeatures, RecommendationFeature.USE_BUSINESS_INFO];
      return DomainsApiClient.queries({
        httpClient
      }).listRecommendations({
        query,
        tlds: tlds ?? [],
        features: query === DEFAULT_QUERY ? features : baseFeatures,
        paging: {
          limit: expandedLimit ?? DEFAULT_LIMIT
        },
        ...(productType ? {
          productType: {
            domainProductType: productType
          }
        } : {})
      });
    }
  });
};
const mergeExactMatchPreview = (recommendations, preview) => {
  if (!preview) {
    return recommendations;
  }
  return recommendations.some(_ref5 => {
    let domain = _ref5.domain;
    return domain === preview.domain;
  }) ? recommendations : [preview, ...recommendations];
};
export const useRecommendations = function (_temp2) {
  var _data$pages;
  let _ref6 = _temp2 === void 0 ? {
      limit: DEFAULT_LIMIT,
      enableOnEmptyQuery: true,
      shouldFetchRecommendations: true,
      uniqueSldsOnly: false
    } : _temp2,
    limit = _ref6.limit,
    _ref6$enableOnEmptyQu = _ref6.enableOnEmptyQuery,
    enableOnEmptyQuery = _ref6$enableOnEmptyQu === void 0 ? true : _ref6$enableOnEmptyQu,
    _ref6$shouldFetchReco = _ref6.shouldFetchRecommendations,
    shouldFetchRecommendations = _ref6$shouldFetchReco === void 0 ? true : _ref6$shouldFetchReco,
    _ref6$uniqueSldsOnly = _ref6.uniqueSldsOnly,
    uniqueSldsOnly = _ref6$uniqueSldsOnly === void 0 ? false : _ref6$uniqueSldsOnly,
    productType = _ref6.productType,
    _ref6$alwaysPaginate = _ref6.alwaysPaginate,
    alwaysPaginate = _ref6$alwaysPaginate === void 0 ? false : _ref6$alwaysPaginate,
    _ref6$enableEarlyExac = _ref6.enableEarlyExactMatch,
    enableEarlyExactMatch = _ref6$enableEarlyExac === void 0 ? false : _ref6$enableEarlyExac;
  const _ref7 = CoreDomainSearchStore.useSubmittedFilters() ?? {},
    tlds = _ref7.tlds,
    submittedFilters = _objectWithoutPropertiesLoose(_ref7, _excluded2);
  const _useState = useState(DEFAULT_FEATURES),
    selectedFeatures = _useState[0],
    setSelectedFeatures = _useState[1];
  const query = createAdvancedQuery(submittedFilters);
  const _ref8 = getPremiumEssentialsSafe() ?? {},
    siteId = _ref8.siteId;
  const isExpandedGtldOn = !alwaysPaginate && isExpandedGtldSuggestionsEnabled();
  const requestLimit = isExpandedGtldOn ? EXPANDED_LIMIT : limit;
  // Inert here — `resolveFeatures` drops PRICING from this call and the ids ride on
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useDomainsAccount({}, true, {
      refetchOnMount: true,
      staleTime: 1000 * 60 * 3
    }),
    domainsAccount = _DomainsApiClient$hoo.data;
  const requestedFeatures = query ? resolveFeatures(selectedFeatures) : [...resolveFeatures(DEFAULT_FEATURES), RecommendationFeature.USE_BUSINESS_INFO];
  const isDirectCallOn = canCallSuggestDomainsDirectly(requestedFeatures);
  const isEnabled = shouldFetchRecommendations && (!!query || enableOnEmptyQuery && !!siteId);
  const _DomainsApiClient$use = DomainsApiClient.useQueries(),
    listRecommendations = _DomainsApiClient$use.listRecommendations;
  const recommendationsQuery = useInfiniteQuery({
    queryKey: getRecommendationsQueryKey({
      query,
      features: resolveFeatures(selectedFeatures),
      tlds,
      hasValidVouchers: domainsAccount == null ? void 0 : domainsAccount.hasValidVouchers,
      uniqueSldsOnly,
      productType,
      limit: isExpandedGtldOn ? requestLimit : undefined
    }),
    enabled: isEnabled && !isDirectCallOn,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async _ref9 => {
      let pageParam = _ref9.pageParam;
      return listRecommendations({
        query: query || DEFAULT_QUERY,
        tlds: tlds ?? [],
        features: requestedFeatures,
        paging: {
          limit: requestLimit,
          cursor: pageParam
        },
        ...(uniqueSldsOnly ? {
          uniqueSldsOnly
        } : {}),
        ...(productType ? {
          productType: {
            domainProductType: productType
          }
        } : {})
      });
    },
    getNextPageParam: lastPage => {
      var _lastPage$pagingMetad, _lastPage$pagingMetad2;
      return lastPage != null && (_lastPage$pagingMetad = lastPage.pagingMetadata) != null && _lastPage$pagingMetad.hasNext ? lastPage == null || (_lastPage$pagingMetad2 = lastPage.pagingMetadata) == null || (_lastPage$pagingMetad2 = _lastPage$pagingMetad2.cursors) == null ? void 0 : _lastPage$pagingMetad2.next : undefined;
    }
  });
  const suggestionsQuery = useSuggestions({
    query: query || DEFAULT_QUERY,
    tlds: tlds ?? [],
    limit: requestLimit,
    uniqueSldsOnly,
    productType,
    enabled: isEnabled && isDirectCallOn
  });
  const _ref0 = isDirectCallOn ? suggestionsQuery : recommendationsQuery,
    data = _ref0.data,
    isLoading = _ref0.isLoading,
    fetchNextPage = _ref0.fetchNextPage,
    hasNextPage = _ref0.hasNextPage,
    isFetchingNextPage = _ref0.isFetchingNextPage,
    queryError = _ref0.error,
    refetch = _ref0.refetch;
  const recommendationPages = (data == null || (_data$pages = data.pages) == null ? void 0 : _data$pages.map(page => page.recommendations ?? [])) ?? [];
  const recommendations = recommendationPages.flat();
  const _useDisplayPrices = useDisplayPrices(recommendationPages, productType),
    pricesByDomain = _useDisplayPrices.pricesByDomain,
    isPricingLoading = _useDisplayPrices.isLoading,
    pricesExcludeTax = _useDisplayPrices.pricesExcludeTax;
  const recommendationsWithPrices = recommendations.map(recommendation => recommendation.domain && pricesByDomain.has(recommendation.domain) ? {
    ...recommendation,
    price: pricesByDomain.get(recommendation.domain)
  } : recommendation);
  const _useExactMatchPreview = useExactMatchPreview({
      enabled: enableEarlyExactMatch && shouldFetchRecommendations,
      productType
    }),
    exactMatchPreview = _useExactMatchPreview.recommendation,
    isPreviewPricingLoading = _useExactMatchPreview.isPricingLoading;
  const mergedRecommendations = mergeExactMatchPreview(recommendationsWithPrices, exactMatchPreview);
  const hasRecommendations = recommendationsWithPrices.length > 0;
  const error = queryError ? classifyRecommendationsError(queryError) : !isLoading && mergedRecommendations.length === 0 ? 'NoRecommendations' : undefined;

  // Only when the user ends up with zero results: a failed fetchNextPage
  // sets queryError too, but earlier pages are still on screen — that is a
  // view-more failure, not a result-page error.
  const biError = recommendations.length > 0 ? undefined : queryError ? 'suggestions_call_failed' : !isLoading && (data == null ? void 0 : data.pages) != null ? 'suggestions_no_results' : undefined;
  const response = {
    recommendations: mergedRecommendations,
    hasNextPage: isExpandedGtldOn ? false : hasNextPage,
    selectedFeatures,
    isLoading,
    isLeadingCardLoading: isLoading && !exactMatchPreview,
    isFetchingNextPage,
    fetchNextPage,
    setSelectedFeatures,
    error,
    biError,
    biErrorSubtype: biError === 'suggestions_call_failed' ? classifyBiErrorSubtype(queryError) : undefined,
    query: query === DEFAULT_QUERY ? undefined : query,
    onRetry: () => refetch(),
    taxesNotIncluded: pricesExcludeTax,
    isPricingLoading: hasRecommendations ? isPricingLoading : isPreviewPricingLoading
  };
  return response;
};
//# sourceMappingURL=useRecommendations.js.map